import React, { useEffect, useState, useCallback } from "react";
import { Download, X, Monitor } from "lucide-react";

// Deteksi perangkat: tampilkan PWA install prompt hanya untuk desktop/tablet.
// Mobile (handphone) sengaja DIKECUALIKAN agar pengguna mobile tidak diminta
// menginstal aplikasi (sesuai requirement: hanya desktop/tablet/iPad).
const isMobileDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad modern memakai UA mirip Mac \u2014 deteksi via touch points.
  const isIPad =
    /iPad/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
  if (isIPad) return false; // iPad dianggap tablet \u2192 ALLOW
  // Smartphone (Android phone, iPhone, mobile UA, dsb.)
  const phoneRegex =
    /Android.+Mobile|iPhone|iPod|Mobile Safari|Opera Mini|IEMobile/i;
  if (phoneRegex.test(ua)) return true;
  // Heuristik lebar layar kecil = mobile
  if (typeof window !== "undefined" && window.innerWidth < 768) return true;
  return false;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
};

const DISMISS_KEY = "pwaInstallDismissedAt";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // tampilkan ulang setelah 7 hari

const wasRecentlyDismissed = () => {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
};

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [eligibleDevice, setEligibleDevice] = useState(false);

  useEffect(() => {
    // Jangan render apa pun di mobile / standalone / setelah dismissed.
    if (isMobileDevice()) return;
    if (isStandalone()) return;
    setEligibleDevice(true);

    const handler = (e) => {
      e.preventDefault();
      if (wasRecentlyDismissed()) return;
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => {
      setVisible(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      }
    } catch {
      /* no-op */
    } finally {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* no-op */
    }
    setVisible(false);
  };

  if (!eligibleDevice || !visible || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[60] max-w-sm w-[calc(100%-2rem)] sm:w-96 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 overflow-hidden">
        <div className="bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-700 p-4 relative">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Tutup">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center backdrop-blur-sm">
              <Monitor className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-100">
                Aplikasi
              </p>
              <h3 className="text-sm font-bold text-white">
                Pasang ASA Link POS
              </h3>
            </div>
          </div>
        </div>
        <div className="p-4">
          <p className="text-xs text-slate-600 leading-relaxed">
            Pasang aplikasi di desktop / tablet Anda untuk akses cepat tanpa
            harus membuka browser. Mode aplikasi memberi pengalaman lebih fokus
            saat melayani transaksi.
          </p>
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors">
              Nanti saja
            </button>
            <button
              onClick={handleInstall}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-lg shadow-sm shadow-teal-600/20 transition-colors">
              <Download className="w-3.5 h-3.5" />
              Install
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
